// Email Verification Page Component
// LazySEM Frontend - Email verification page

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import styles from './VerifyEmail.module.css';

import { logger } from '../../utils/logger';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmail, isAuthenticated, isLoading } = useAuth();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [hasVerified, setHasVerified] = useState(false);

  const goAfterVerification = () => {
    if (isLoading) return;

    if (isAuthenticated) {
      navigate('/auth/continue', { replace: true });
      return;
    }

    navigate('/login', {
      replace: true,
      state: { verified: true },
    });
  };

  useEffect(() => {
    // Prevent multiple calls
    if (hasVerified) return;

    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setErrorMessage('Verification token is missing from the URL');
      setHasVerified(true);
      return;
    }

    const verify = async () => {
      try {
        setStatus('verifying');
        await verifyEmail(token);
        setStatus('success');
        setHasVerified(true);
      } catch (error: unknown) {
        logger.error('Email verification error:', error);
        setStatus('error');
        setErrorMessage(
          error instanceof Error && error.message
            ? error.message
            : 'Failed to verify email. The link may be invalid or expired.',
        );
        setHasVerified(true);
      }
    };

    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  useEffect(() => {
    if (status !== 'success' || isLoading) return;

    const timeoutId = window.setTimeout(goAfterVerification, 3000);
    return () => window.clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, isLoading, isAuthenticated]);

  return (
    <div className={styles.container}>
      <div className={styles.formWrapper}>
        <div className={styles.header}>
          {status === 'verifying' && (
            <>
              <div className={styles.spinner}></div>
              <h1 className={styles.title}>Verifying Your Email</h1>
              <p className={styles.subtitle}>Please wait while we verify your email address...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className={styles.successIcon}>✓</div>
              <h1 className={styles.title}>Email Verified!</h1>
              <p className={styles.subtitle}>Your email has been successfully verified.</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className={styles.errorIcon}>✕</div>
              <h1 className={styles.title}>Verification Failed</h1>
              <p className={styles.subtitle}>{errorMessage}</p>
            </>
          )}
        </div>

        {status === 'success' && (
          <div className={styles.content}>
            <p className={styles.redirectMessage}>
              {isLoading
                ? 'Finalizing your session...'
                : isAuthenticated
                  ? 'Continuing to your account in 3 seconds...'
                  : 'Email verified. Redirecting to login in 3 seconds...'}
            </p>
            <button
              className={styles.submitButton}
              onClick={goAfterVerification}
              disabled={isLoading}
            >
              {isAuthenticated ? 'Continue Now' : 'Go to Login Now'}
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className={styles.content}>
            <button
              className={styles.submitButton}
              onClick={() => navigate('/')}
            >
              Back to Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;

